import { MailCheck, Eye, MousePointerClick, AlertTriangle } from "lucide-react";

/**
 * Compact email tracking pill shown next to a "Sent" devis status.
 * Hierarchy: bounced > clicked > opened > delivered > nothing.
 * Returns null if no Resend event has been received yet.
 *
 * Props:
 *  - devis: object with email_delivered_at, email_opened_at, email_opened_count,
 *           email_clicked_at, email_clicked_count, email_bounced_at, email_bounce_reason
 *  - testid: optional data-testid
 */
export default function EmailTrackingBadge({ devis, testid }) {
  if (!devis) return null;

  const fmtRelative = (iso) => {
    if (!iso) return "";
    const diffMs = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(diffMs) || diffMs < 0) return "";
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return "à l'instant";
    if (m < 60) return `il y a ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `il y a ${h} h`;
    const d = Math.floor(h / 24);
    if (d < 30) return `il y a ${d} j`;
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  };

  let icon = null;
  let label = "";
  let tone = "";
  let title = "";

  if (devis.email_bounced_at) {
    icon = <AlertTriangle size={11} />;
    label = "Rebondi";
    tone = "border-destructive/40 text-destructive bg-destructive/5";
    title = devis.email_bounce_reason
      ? `Rebondi · ${devis.email_bounce_reason} · ${fmtRelative(devis.email_bounced_at)}`
      : `Rebondi ${fmtRelative(devis.email_bounced_at)}`;
  } else if (devis.email_clicked_at) {
    icon = <MousePointerClick size={11} />;
    const n = devis.email_clicked_count || 1;
    label = n > 1 ? `Cliqué ${n}×` : "Cliqué";
    tone = "border-primary/50 text-primary bg-primary/5";
    title = `Lien cliqué · ${fmtRelative(devis.email_clicked_at)}`;
  } else if (devis.email_opened_at) {
    icon = <Eye size={11} />;
    const n = devis.email_opened_count || 1;
    label = n > 1 ? `Ouvert ${n}×` : "Ouvert";
    tone = "border-primary/40 text-primary bg-primary/[0.04]";
    title = `Ouvert · ${fmtRelative(devis.email_opened_at)}`;
  } else if (devis.email_delivered_at) {
    icon = <MailCheck size={11} />;
    label = "Livré";
    tone = "border-foreground/15 text-muted-foreground bg-foreground/[0.02]";
    title = `Livré · ${fmtRelative(devis.email_delivered_at)}`;
  } else {
    return null;
  }

  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 px-2 py-0.5 border ${tone} text-[10px] tracking-[0.16em] uppercase font-medium`}
      data-testid={testid || "email-tracking-badge"}
    >
      {icon}
      {label}
    </span>
  );
}
